import { useEffect, useRef } from 'react';
import { useAppSelector } from '@/hooks';
import { PushNotificationService } from '@/services';
import { supabase } from '@/lib/supabase';
import { IS_MOBILE_APP } from '@/constants/env';
import { logger } from '@/lib/logger';

/**
 * Auto-initializes push notifications on app startup when:
 * 1. User is authenticated
 * 2. User previously enabled notifications (enable_notifications in users table)
 * 3. Device permission is granted
 *
 * This ensures FCM listeners are re-established after app restart
 * and device tokens stay up to date.
 */
export const usePushNotificationInit = () => {
  const user = useAppSelector((state) => state.auth.user);
  const initAttempted = useRef(false);

  useEffect(() => {
    if (!user?.id || initAttempted.current) return;
    initAttempted.current = true;

    const autoInit = async () => {
      try {
        // Check if user has notifications enabled
        const { data, error } = await supabase
          .from('users')
          .select('enable_notifications')
          .eq('uid', user.id)
          .single();

        if (error || !data?.enable_notifications) return;

        // Check if permission is already granted (don't prompt)
        const permission = await PushNotificationService.checkPermissions();
        if (permission.receive !== 'granted') return;

        // Fire-and-forget: re-establish listeners and refresh token in background
        PushNotificationService.initialize(user.id)
          .then(() => logger.info('Auto-initialized on app startup', { component: 'PushNotificationInit' }))
          .catch((err) => logger.warn('Auto-init failed', { error: err, component: 'PushNotificationInit' }));
      } catch (error) {
        logger.warn('Auto-init preflight failed', { error, component: 'PushNotificationInit' });
      }
    };

    autoInit();
  }, [user?.id]);
};
